import { List, Map } from 'immutable';
import { format } from 'date-fns';
import { constants as logConstants } from '@saucelabs/sl-component-log';

import types from './types';
import * as globalTypes from '../../types';
import * as constants from './constants';
import { LogLineRecord } from './records';

const { LEVELS, LEVELS_BY_SEVERITY, POSITIONS } = logConstants;

const initialState = Map({
  active: false,
  app: null,
  appName: null,
  apps: null,
  bottomHasMore: false,
  filter: Map({
    ignoreCase: true,
    invert: false,
    phrase: '',
    regex: false,
  }),
  displayedLines: List(),
  lines: List(),
  logLevel: LEVELS.WARN,
  paused: false,
  processIds: List(),
  searchExpanded: false,
  startIndex: 0,
  textWrapped: false,
  topHasMore: false,
});

const LOAD_STEP = 10;
const LIMIT = constants.MOBILE_LOG_LINES_LIMIT;

function adjustIndexToBoundaries(desiredStartIndex, size) {
  const bigEnough = desiredStartIndex >= 0;
  const smallEnough = desiredStartIndex <= size - LIMIT;

  if (!bigEnough) {
    return 0;
  }

  if (!smallEnough) {
    // adjust again as it may be too small
    return adjustIndexToBoundaries(size - LIMIT, size);
  }

  return desiredStartIndex;
}

const filterByLogLevel = (lines, logLevel) => {
  const visibleLogLevels = LEVELS_BY_SEVERITY.slice(LEVELS_BY_SEVERITY.indexOf(LEVELS[logLevel]));

  return lines.filter((line) => visibleLogLevels.includes(line.level));
};

const filterByProcessId = (lines, processIds) => {
  if (processIds.length === 0) {
    return lines;
  }

  return lines.filter((line) => line.processId && processIds.contains(line.processId));
};

const filterByPhrase = (lines, filter) => {
  if (!filter.get('phrase')) return lines;

  let matchFunction;

  if (filter.get('regex')) {
    const regex = new RegExp(filter.get('phrase'), filter.get('ignoreCase') ? 'i' : '');
    matchFunction = (line) => line.search(regex) >= 0;
  } else if (filter.get('ignoreCase')) {
    const phrase = filter.get('phrase').toLowerCase();
    matchFunction = (line) => line.toLowerCase().includes(phrase);
  } else {
    matchFunction = (line) => line.includes(filter.get('phrase'));
  }

  return lines.filter((line) => matchFunction(line.message) !== filter.get('invert'));
};

const getDisplayedLines = (lines, logLevel, filters, filterType, processIds) => {
  if (constants.MOBILE_APP_TYPE_FILTER === filterType) {
    return filterByProcessId(filterByPhrase(
      filterByLogLevel(lines, logLevel),
      filters,
    ), processIds);
  }

  return filterByPhrase(
    filterByLogLevel(lines, logLevel),
    filters,
  );
};

const isJson = (input) => {
  try {
    return typeof input === 'object' && input !== null && input.message;
  } catch (ignored) {
    return false;
  }
};

const parseLogLine = (input, key) => {
  // Message format:
  // "2019-09-02 14:27:41.131\tDEBUG:\tBattCtrl : onReceive - ACTION_BATTERY_CHANGED : foo = 86"
  const newLineValues = input.split('\t');
  const dateValueInISO8601 = newLineValues[0].replace(' ', 'T'); // Required for "new Date()" to work in Safari
  // Some of the messages contain tabs themselves. In that case the first 2 tabs are removed
  const message = newLineValues.length === 3 ? newLineValues[2] : input.replace(/^.*?\t.*?\t/, '');
  return new LogLineRecord({
    key,
    level: newLineValues[1].replace(':', ''),
    message,
    time: format(new Date(dateValueInISO8601), constants.MOBILE_LOG_TIME_FORMAT),
    timestamp: dateValueInISO8601,
    processId: null,
  });
};

const parseJsonLogLine = (input, key) => {
  const newLineValues = input.message.split('\t');
  const dateValueInISO8601 = input.timestamp.replace(' ', 'T'); // Required for "new Date()" to work in Safari
  const message = newLineValues.length === 3 ? newLineValues[2] : input.message.replace(/^.*?\t.*?\t/, '');

  return new LogLineRecord({
    key,
    level: input.level,
    message,
    time: format(new Date(dateValueInISO8601), constants.MOBILE_LOG_TIME_FORMAT),
    timestamp: dateValueInISO8601,
    processId: input.processId,
  });
};

const mobileLogReducer = (state = initialState, { type, payload }) => {
  const prevLines = state.get('lines');

  switch (type) {
    case types.MOBILE_LOG_LINE_ADD: {
      const key = prevLines.size + 1;
      const json = isJson(payload);
      const newLine = json ? parseJsonLogLine(payload, key) : parseLogLine(payload, key);
      const allLines = prevLines.push(newLine);

      const match = newLine.message.match(/RDCIdentifier: \[(.*)\]/);
      if (match && json) {
        const APP = constants.MOBILE_APP_TYPE_FILTER;
        const previousIds = state.get('processIds');
        const newProcessIds = previousIds.push(newLine.processId);
        const newName = state.get('appName') || `${match[1]}`;

        return state
          .set('app', APP)
          .set('apps', { ALL: 'All Apps', APP: newName })
          .set('processIds', newProcessIds)
          .set('logLevel', LEVELS.WARN)
          .set('lines', allLines);
      }

      return state.set('lines', allLines);
    }
    case types.MOBILE_LOG_UPDATE_DISPLAYED_LINES: {
      const displayedLines = getDisplayedLines(prevLines, state.get('logLevel'), state.get('filter'), state.get('app'), state.get('processIds'));
      const startIndex = state.get('paused') ?
        state.get('startIndex') :
        adjustIndexToBoundaries(displayedLines.size - LIMIT, displayedLines.size);

      return state.withMutations((mutableState) => (
        mutableState
          .set('startIndex', startIndex)
          .set('topHasMore', startIndex > 0)
          .set('bottomHasMore', displayedLines.size > startIndex + LIMIT)
          .set('displayedLines', displayedLines.slice(startIndex, startIndex + LIMIT))
      ));
    }
    case types.MOBILE_LOG_LINES_LOAD_MORE: {
      let startIndex = state.get('startIndex');
      let paused = true;

      if (payload === POSITIONS.TOP) {
        startIndex = adjustIndexToBoundaries(startIndex - LOAD_STEP, prevLines.size);
      }

      if (payload === POSITIONS.BOTTOM) {
        startIndex = adjustIndexToBoundaries(startIndex + LOAD_STEP, prevLines.size);
        paused = startIndex < prevLines.size - LIMIT;
      }

      return state.set('startIndex', startIndex).set('paused', paused);
    }
    case types.MOBILE_LOG_LINES_RESUME: {
      const startIndex = adjustIndexToBoundaries(prevLines.size - LIMIT, prevLines.size);

      return state.set('startIndex', startIndex).set('paused', false);
    }
    case types.MOBILE_LOG_LINES_PAUSE:
      return state.set('paused', true);
    case types.MOBILE_LOG_SET_LOG_LEVEL:
      return state.set('logLevel', payload).set('startIndex', 0);
    case types.MOBILE_LOG_CLEAR:
      return state.withMutations((mutableState) => (
        mutableState
          .set('bottomHasMore', initialState.get('bottomHasMore'))
          .set('displayedLines', initialState.get('displayedLines'))
          .set('lines', initialState.get('lines'))
          .set('startIndex', initialState.get('startIndex'))
          .set('topHasMore', initialState.get('topHasMore'))
      ));
    case types.MOBILE_LOG_EXPAND_SEARCH:
      return state.set('searchExpanded', !state.get('searchExpanded'));
    case types.MOBILE_LOG_WRAP_TEXT:
      return state.set('textWrapped', !state.get('textWrapped'));
    case types.MOBILE_LOG_CHANGE_APP:
      return state.set('app', payload).set('startIndex', 0);
    case types.MOBILE_LOG_UPDATE_SESSION: {
      const name = payload?.get('appVersion')?.getDisplayName() ?? null;

      return state.set('appName', name);
    }
    case types.MOBILE_LOG_UPDATE_FILTERS: {
      const allLines = state.get('lines');
      const filter = state.get('filter').merge(payload);
      const displayedLines = getDisplayedLines(allLines, state.get('logLevel'), filter, state.get('app'), state.get('processIds'));

      const startIndex = state.get('paused') ?
        state.get('startIndex') :
        adjustIndexToBoundaries(displayedLines.size - LIMIT, displayedLines.size);

      return state.withMutations((mutableState) => (
        mutableState
          .set('filter', filter)
          .set('startIndex', startIndex)
          .set('topHasMore', startIndex > 0)
          .set('bottomHasMore', displayedLines.size > startIndex + LIMIT)
          .set('displayedLines', displayedLines.slice(startIndex, startIndex + LIMIT))
      ));
    }
    case globalTypes.MOBILE_SESSION_STOP.SUCCESS:
      return initialState;
    default:
      return state;
  }
};

export default mobileLogReducer;
export { initialState };
